import type { BudgetRuleAssociatedCampaign } from '@/utils/budgetRuleCampaigns';
import type { SpCampaignNormalized } from '@/types/spCampaigns';

type BudgetRuleLinkedCampaignsChipsProps = {
  associated: BudgetRuleAssociatedCampaign[];
  profileCampaigns: SpCampaignNormalized[];
  isLoading?: boolean;
  error?: Error | null;
};

function resolveName(
  row: BudgetRuleAssociatedCampaign,
  profileCampaigns: SpCampaignNormalized[]
): string {
  if (row.campaignName) return row.campaignName;
  const match = profileCampaigns.find((c) => c.campaignId === row.campaignId);
  return match?.name ?? row.campaignId;
}

export function BudgetRuleLinkedCampaignsChips({
  associated,
  profileCampaigns,
  isLoading = false,
  error = null,
}: BudgetRuleLinkedCampaignsChipsProps) {
  if (isLoading) {
    return <p className="text-sm text-foreground-subtle">Loading associated campaigns…</p>;
  }
  if (error) {
    return (
      <p className="text-sm text-error">
        {error.message || 'Failed to load associated campaigns'}
      </p>
    );
  }
  if (associated.length === 0) {
    return <p className="text-sm text-foreground-subtle">Not linked to any campaigns.</p>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {associated.map((row) => (
        <span
          key={row.campaignId}
          title={row.campaignId}
          className="inline-flex max-w-full items-center gap-1 rounded-full border border-brand/25 bg-brand-subtle px-2.5 py-1 text-xs font-medium text-foreground"
        >
          <span className="truncate">{resolveName(row, profileCampaigns)}</span>
          {row.ruleStatus ? (
            <span className="shrink-0 text-[10px] font-normal text-foreground-subtle">
              · {row.ruleStatus}
            </span>
          ) : null}
        </span>
      ))}
    </div>
  );
}
